// src/components/QrDirectChat.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../firebase';
import {
    collection,
    doc,
    addDoc,
    setDoc,
    onSnapshot,
    serverTimestamp,
    query,
    orderBy,
    limit
} from 'firebase/firestore';
import {
    FaComments,
    FaPaperPlane,
    FaUser,
    FaSpinner,
    FaTimes,
    FaExpand,
    FaCompress,
    FaBell,
    FaCar,
    FaArrowLeft,
    FaSearch
} from 'react-icons/fa';
import {
    getNotificationPermission,
    requestNotificationPermission,
    triggerWebNotification
} from '../lib/webNotification';

// Curated distinctive avatar colors and accents for different visitor nicknames
const NICKNAME_PALETTES = [
    { bg: 'bg-emerald-500', text: 'text-emerald-700', border: 'border-emerald-200', lightBg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-800' },
    { bg: 'bg-indigo-500', text: 'text-indigo-700', border: 'border-indigo-200', lightBg: 'bg-indigo-50', badge: 'bg-indigo-100 text-indigo-800' },
    { bg: 'bg-amber-500', text: 'text-amber-700', border: 'border-amber-200', lightBg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-800' },
    { bg: 'bg-rose-500', text: 'text-rose-700', border: 'border-rose-200', lightBg: 'bg-rose-50', badge: 'bg-rose-100 text-rose-800' },
    { bg: 'bg-cyan-500', text: 'text-cyan-700', border: 'border-cyan-200', lightBg: 'bg-cyan-50', badge: 'bg-cyan-100 text-cyan-800' },
    { bg: 'bg-purple-500', text: 'text-purple-700', border: 'border-purple-200', lightBg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-800' },
    { bg: 'bg-teal-500', text: 'text-teal-700', border: 'border-teal-200', lightBg: 'bg-teal-50', badge: 'bg-teal-100 text-teal-800' },
    { bg: 'bg-orange-500', text: 'text-orange-700', border: 'border-orange-200', lightBg: 'bg-orange-50', badge: 'bg-orange-100 text-orange-800' }
];

const getNicknameStyle = (name = '') => {
    if (!name) return NICKNAME_PALETTES[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % NICKNAME_PALETTES.length;
    return NICKNAME_PALETTES[index];
};

/**
 * Premium Direct Real-Time Chat Widget between Visitors and the Vehicle Owner.
 * Supports:
 * - Floating interactive bubble option on preview / scan landing
 * - Full-screen overlay portal dialog to document.body (like Shopier modal)
 * - Isolated per-visitor chat channels:
 *   * Vehicle Owner sees a list/inbox of different visitors (chatboxes) and clicks a user to open their thread
 *   * Each visitor only sees and chats inside their own channel
 * - Real-time Web Notifications with custom chime
 */
const QrDirectChat = ({ qrCodeId, profileData, isOwner = false, defaultMode = 'bubble' }) => {
    const isChatAllowed = profileData?.allowDirectChat !== false;

    // View State
    const [isOpen, setIsOpen] = useState(defaultMode === 'fullscreen');
    const [isFullscreen, setIsFullscreen] = useState(defaultMode === 'fullscreen');

    // Visitor nickname stored in localStorage
    const [nickname, setNickname] = useState(() => {
        return localStorage.getItem('kt_qr_nickname') || '';
    });
    const [tempNickname, setTempNickname] = useState(nickname);
    const [hasSetNickname, setHasSetNickname] = useState(() => Boolean(nickname) || isOwner);

    // Active visitor thread selected by owner
    const [activeVisitor, setActiveVisitor] = useState(null);
    const [searchFilter, setSearchFilter] = useState('');

    // Messages state
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [inputText, setInputText] = useState('');
    const [sending, setSending] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // Web Notification state
    const [notificationPerm, setNotificationPerm] = useState(() => getNotificationPermission());
    const [showPermBanner, setShowPermBanner] = useState(false);

    const messagesEndRef = useRef(null);
    const initialLoadDone = useRef(false);

    // Prevent background scrolling when in full-screen mode
    useEffect(() => {
        if (isFullscreen && isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isFullscreen, isOpen]);

    // Check & request notification permission when component loads
    useEffect(() => {
        const perm = getNotificationPermission();
        setNotificationPerm(perm);

        if (perm === 'default') {
            setShowPermBanner(true);
            requestNotificationPermission().then((res) => {
                setNotificationPerm(res);
                if (res !== 'default') {
                    setShowPermBanner(false);
                }
            });
        }
    }, []);

    const handleGrantNotification = async () => {
        const res = await requestNotificationPermission();
        setNotificationPerm(res);
        setShowPermBanner(false);
    };

    // 1. Listen for real-time messages under this specific QR code
    useEffect(() => {
        if (!qrCodeId) return;

        const cleanQr = qrCodeId.trim().toUpperCase();
        const messagesCol = collection(db, 'qrChats', cleanQr, 'messages');
        const q = query(messagesCol, orderBy('timestamp', 'asc'), limit(150));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            }));

            // Check for new incoming messages after initial load
            if (initialLoadDone.current && list.length > 0) {
                const latest = list[list.length - 1];
                const isIncomingForUser = isOwner
                    ? latest.senderRole === 'visitor'
                    : latest.senderRole === 'owner' && (latest.recipientVisitor === nickname || !latest.recipientVisitor);

                if (isIncomingForUser) {
                    if (!isOpen) {
                        setUnreadCount(prev => prev + 1);
                    }

                    triggerWebNotification({
                        title: isOwner
                            ? `Yeni Mesaj: ${latest.senderName || 'Ziyaretçi'}`
                            : `${profileData?.vehiclePlate || 'Araç Sahibi'} Yanıtladı`,
                        body: latest.text,
                        onClick: () => {
                            setIsOpen(true);
                            setIsFullscreen(true);
                            if (isOwner && latest.senderRole === 'visitor') {
                                setActiveVisitor(latest.senderName);
                            }
                        }
                    });
                }
            } else {
                initialLoadDone.current = true;
            }

            setMessages(list);
            setLoadingMessages(false);
        }, (err) => {
            console.warn("QrDirectChat snapshot listener error:", err);
            setLoadingMessages(false);
        });

        return () => unsubscribe();
    }, [qrCodeId, isOwner, isOpen, profileData, nickname]);

    // Clear unread count when opened
    useEffect(() => {
        if (isOpen) {
            setUnreadCount(0);
        }
    }, [isOpen]);

    // Auto-scroll to bottom of messages
    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen, activeVisitor]);

    // Group threads for the vehicle owner view
    const threads = useMemo(() => {
        const threadMap = {};

        messages.forEach(msg => {
            // Determine which visitor thread this belongs to
            let threadKey = null;
            if (msg.senderRole === 'visitor') {
                threadKey = msg.senderName;
            } else if (msg.senderRole === 'owner' && msg.recipientVisitor) {
                threadKey = msg.recipientVisitor;
            }

            if (!threadKey) return;

            if (!threadMap[threadKey]) {
                threadMap[threadKey] = {
                    visitorName: threadKey,
                    messages: [],
                    lastMessage: msg.text,
                    lastTimestamp: msg.timestamp,
                    unread: 0
                };
            }

            threadMap[threadKey].messages.push(msg);
            threadMap[threadKey].lastMessage = msg.text;
            threadMap[threadKey].lastTimestamp = msg.timestamp;
        });

        return Object.values(threadMap).sort((a, b) => {
            const timeA = a.lastTimestamp?.toMillis ? a.lastTimestamp.toMillis() : 0;
            const timeB = b.lastTimestamp?.toMillis ? b.lastTimestamp.toMillis() : 0;
            return timeB - timeA;
        });
    }, [messages]);

    // Automatically select the most recent thread if owner hasn't selected any yet
    useEffect(() => {
        if (isOwner && !activeVisitor && threads.length > 0) {
            // Keep activeVisitor null so owner sees thread list initially, or can pick one
        }
    }, [isOwner, activeVisitor, threads]);

    // Filter messages for current view
    const visibleMessages = useMemo(() => {
        if (isOwner) {
            if (!activeVisitor) return [];
            return messages.filter(m => 
                (m.senderRole === 'visitor' && m.senderName === activeVisitor) ||
                (m.senderRole === 'owner' && (m.recipientVisitor === activeVisitor || !m.recipientVisitor))
            );
        } else {
            // Visitor only sees their own chat channel!
            if (!nickname) return [];
            return messages.filter(m => 
                (m.senderRole === 'visitor' && m.senderName === nickname) ||
                (m.senderRole === 'owner' && (m.recipientVisitor === nickname || !m.recipientVisitor))
            );
        }
    }, [messages, isOwner, activeVisitor, nickname]);

    // Save visitor nickname
    const handleSaveNickname = (e) => {
        e?.preventDefault();
        const trimmed = tempNickname.trim();
        if (!trimmed) return;
        localStorage.setItem('kt_qr_nickname', trimmed);
        setNickname(trimmed);
        setHasSetNickname(true);
    };

    // Send a message
    const handleSendMessage = async (textToSend = null) => {
        const text = (textToSend || inputText).trim();
        if (!text || sending) return;

        const senderName = isOwner ? 'Araç Sahibi' : (nickname || 'Ziyaretçi');
        const cleanQr = qrCodeId.trim().toUpperCase();
        const recipient = isOwner ? activeVisitor : null;

        setSending(true);
        try {
            const messagesCol = collection(db, 'qrChats', cleanQr, 'messages');
            await addDoc(messagesCol, {
                text,
                senderName,
                senderRole: isOwner ? 'owner' : 'visitor',
                recipientVisitor: recipient || '',
                timestamp: serverTimestamp()
            });

            // Update parent chat summary
            const chatDoc = doc(db, 'qrChats', cleanQr);
            await setDoc(chatDoc, {
                qrCodeId: cleanQr,
                vehiclePlate: profileData?.vehiclePlate || '',
                ownerId: profileData?.userId || '',
                lastMessage: text,
                lastSender: senderName,
                lastSenderRole: isOwner ? 'owner' : 'visitor',
                updatedAt: serverTimestamp()
            }, { merge: true });

            if (!textToSend) {
                setInputText('');
            }
        } catch (err) {
            console.error("Error sending QR direct chat message:", err);
            alert("Mesaj gönderilemedi: " + (err.message || 'Lütfen tekrar deneyin.'));
        } finally {
            setSending(false);
        }
    };

    const quickMessages = [
        "Aracınızı biraz çeker misiniz?",
        "Farlarınız açık kalmış.",
        "Camınız hafif aralık.",
        "Araç alarmı çalıyor."
    ];

    // If chat is turned OFF by owner
    if (!isChatAllowed && !isOwner) {
        return (
            <div className="p-4 rounded-2xl bg-gray-50 border border-dashed border-gray-200 text-center text-xs text-gray-400">
                <span>🔒 Araç sahibi doğrudan mesajlaşmayı devre dışı bırakmıştır.</span>
            </div>
        );
    }

    // --- RENDER FLOATING BUBBLE TRIGGER ---
    const renderFloatingBubble = () => {
        const bubbleContent = (
            <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-2 pointer-events-auto">
                {unreadCount > 0 && (
                    <div className="bg-gray-900 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full shadow-lg animate-bounce flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                        <span>{unreadCount} yeni mesajınız var!</span>
                    </div>
                )}

                <button
                    onClick={() => {
                        setIsOpen(true);
                        setIsFullscreen(true);
                    }}
                    className="relative group flex items-center gap-3 px-4 py-3.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-full shadow-2xl transition-all duration-200 border-2 border-white/20 cursor-pointer"
                    aria-label="Canlı Mesajlaşma Baloncuğu"
                >
                    <div className="relative flex items-center justify-center">
                        <FaComments size={22} className="group-hover:scale-110 transition-transform" />
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-blue-600 animate-ping"></span>
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-blue-600"></span>
                    </div>
                    <div className="text-left pr-1 hidden sm:block">
                        <p className="text-xs font-bold leading-tight">
                            {isOwner ? 'Gelen Mesaj Kutusu' : 'Araç Sahibine Yaz'}
                        </p>
                        <p className="text-[10px] text-blue-100 opacity-90 leading-tight">Canlı Mesajlaşma</p>
                    </div>

                    {unreadCount > 0 && (
                        <span className="absolute -top-2 -left-2 bg-red-500 text-white text-[11px] font-black w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-white shadow-md">
                            {unreadCount}
                        </span>
                    )}
                </button>
            </div>
        );

        return typeof document !== 'undefined' ? createPortal(bubbleContent, document.body) : bubbleContent;
    };

    // --- RENDER FULLSCREEN MODAL ---
    const renderFullscreenModal = () => {
        if (!isOpen) return null;

        const filteredThreads = threads.filter(t => 
            t.visitorName.toLowerCase().includes(searchFilter.toLowerCase())
        );

        const modalContent = (
            <div
                className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-md flex flex-col justify-end sm:justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200"
                onClick={() => setIsOpen(false)}
            >
                {/* Modal Container */}
                <div
                    className="w-full sm:max-w-2xl bg-white h-[94vh] sm:h-[680px] max-h-[96vh] rounded-t-3xl sm:rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] flex flex-col overflow-hidden animate-in slide-in-from-bottom-6 duration-200 border border-white/80"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-4 bg-gray-900 text-white flex items-center justify-between select-none">
                        <div className="flex items-center gap-3">
                            {/* Back button for owner if inside a thread */}
                            {isOwner && activeVisitor && (
                                <button
                                    type="button"
                                    onClick={() => setActiveVisitor(null)}
                                    className="p-2 -ml-1 rounded-xl bg-gray-800 hover:bg-gray-700 text-white transition-colors"
                                    title="Sohbet Listesine Dön"
                                >
                                    <FaArrowLeft size={13} />
                                </button>
                            )}

                            <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-lg shadow-sm">
                                <FaComments />
                            </div>
                            <div>
                                <h4 className="text-sm font-bold tracking-wide flex items-center gap-2">
                                    <span>
                                        {isOwner
                                            ? (activeVisitor ? `${activeVisitor} ile Sohbet` : 'Gelen Ziyaretçi Mesaj Kutuları')
                                            : 'Araç Sahibiyle Canlı Mesajlaş'}
                                    </span>
                                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                </h4>
                                <p className="text-[11px] text-gray-300">
                                    {profileData?.vehiclePlate ? (
                                        <span className="inline-flex items-center gap-1">
                                            <FaCar size={10} /> {profileData.vehiclePlate}
                                        </span>
                                    ) : (
                                        `QR: ${qrCodeId}`
                                    )}
                                    {isOwner 
                                        ? (activeVisitor ? ` • Özel Sohbet Kanalı` : ` • ${threads.length} Aktif Kullanıcı`)
                                        : ' • Numara gizli'}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-1.5 text-gray-300">
                            <button
                                type="button"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className="p-2 hover:bg-gray-800 rounded-xl transition-colors text-gray-300 hover:text-white"
                                title={isFullscreen ? "Küçült" : "Tam Ekran Yap"}
                            >
                                {isFullscreen ? <FaCompress size={14} /> : <FaExpand size={14} />}
                            </button>

                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="p-2 hover:bg-gray-800 rounded-xl transition-colors text-gray-300 hover:text-white"
                                title="Kapat"
                            >
                                <FaTimes size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Notification Permission Banner */}
                    {showPermBanner && notificationPerm === 'default' && (
                        <div className="bg-blue-50 border-b border-blue-100 px-4 py-2.5 flex items-center justify-between text-xs text-blue-900">
                            <div className="flex items-center gap-2">
                                <FaBell className="text-blue-600 flex-shrink-0" />
                                <span>Yeni mesajları anında bildirim olarak almak ister misiniz?</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleGrantNotification}
                                    className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-[11px] transition-colors"
                                >
                                    İzin Ver
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowPermBanner(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xs p-1"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Chat Body */}
                    <div className="flex-1 flex flex-col p-4 bg-gray-50 overflow-hidden">
                        {/* CASE 1: Visitor Nickname Gate */}
                        {!isOwner && !hasSetNickname ? (
                            <form onSubmit={handleSaveNickname} className="my-auto p-5 rounded-2xl bg-white border border-blue-100 shadow-sm space-y-3.5">
                                <div className="flex items-center gap-2.5 text-blue-900 text-sm font-bold">
                                    <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        <FaUser />
                                    </div>
                                    <span>Rumuz veya Adınızı Belirleyin</span>
                                </div>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                    Araç sahibinin sizi ayırt edebilmesi ve mesajlarınıza özel dönüş yapabilmesi için lütfen adınızı veya kısa bir tanıtıcı rumuz girin (örn: <em>Komşu Kemal, Kırmızı Polo, Kurye</em>). Mesajlarınız yalnızca sizinle araç sahibi arasında gizli kalır.
                                </p>
                                <div className="flex gap-2 pt-1">
                                    <input
                                        type="text"
                                        value={tempNickname}
                                        onChange={(e) => setTempNickname(e.target.value)}
                                        placeholder="Adınız veya Rumuzunuz..."
                                        maxLength={30}
                                        className="flex-1 px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                        autoFocus
                                        required
                                    />
                                    <button
                                        type="submit"
                                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
                                    >
                                        Kaydet & Başla
                                    </button>
                                </div>
                            </form>
                        ) : isOwner && !activeVisitor ? (
                            /* CASE 2: Vehicle Owner Thread Inbox List (Every user is a different chatbox) */
                            <div className="flex-1 flex flex-col overflow-hidden space-y-3">
                                {/* Search box if multiple visitors */}
                                {threads.length > 2 && (
                                    <div className="relative">
                                        <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                                        <input
                                            type="text"
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                            placeholder="Ziyaretçi adına göre ara..."
                                            className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}

                                <div className="flex items-center justify-between px-1">
                                    <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                                        Gelen Mesaj Kutuları ({threads.length})
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                        Sohbeti açmak için kullanıcıya tıklayın
                                    </span>
                                </div>

                                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                                    {loadingMessages ? (
                                        <div className="h-full flex items-center justify-center text-gray-400 text-xs gap-2">
                                            <FaSpinner className="animate-spin text-blue-600" />
                                            <span>Mesajlar yükleniyor...</span>
                                        </div>
                                    ) : threads.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-center p-8 text-gray-400 text-xs">
                                            <div className="w-14 h-14 rounded-3xl bg-blue-50 text-blue-500 flex items-center justify-center mb-3 text-xl">
                                                <FaComments />
                                            </div>
                                            <p className="font-bold text-gray-700 text-sm">Henüz Mesaj Gelmedi</p>
                                            <p className="text-[11px] mt-1 text-gray-400 max-w-xs">
                                                Aracınızın QR kodunu okutan kişilerin gönderdiği mesajlar burada ayrı kutular halinde listelenecektir.
                                            </p>
                                        </div>
                                    ) : filteredThreads.length === 0 ? (
                                        <div className="text-center py-8 text-gray-400 text-xs">
                                            Aramanıza uygun mesaj kutusu bulunamadı.
                                        </div>
                                    ) : (
                                        filteredThreads.map((thread) => {
                                            const style = getNicknameStyle(thread.visitorName);
                                            const timeStr = thread.lastTimestamp?.toDate
                                                ? thread.lastTimestamp.toDate().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                                                : '';

                                            return (
                                                <div
                                                    key={thread.visitorName}
                                                    onClick={() => setActiveVisitor(thread.visitorName)}
                                                    className="p-3.5 bg-white rounded-2xl border border-gray-200/80 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer flex items-center justify-between group active:scale-[0.99]"
                                                >
                                                    <div className="flex items-center gap-3 overflow-hidden">
                                                        <div className={`w-10 h-10 rounded-2xl ${style.bg} text-white font-bold text-sm flex items-center justify-center flex-shrink-0 shadow-xs uppercase`}>
                                                            {thread.visitorName.slice(0, 2)}
                                                        </div>
                                                        <div className="overflow-hidden">
                                                            <div className="flex items-center gap-2">
                                                                <h5 className="text-xs font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                                                    {thread.visitorName}
                                                                </h5>
                                                                <span className="text-[10px] bg-blue-50 text-blue-600 font-semibold px-2 py-0.2 rounded-full">
                                                                    {thread.messages.length} mesaj
                                                                </span>
                                                            </div>
                                                            <p className="text-[11px] text-gray-500 truncate mt-0.5 max-w-[280px]">
                                                                {thread.lastMessage}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="text-right flex-shrink-0 pl-2">
                                                        <span className="text-[10px] text-gray-400 block mb-1">
                                                            {timeStr}
                                                        </span>
                                                        <span className="text-[11px] text-blue-600 font-bold group-hover:translate-x-1 inline-block transition-transform">
                                                            Sohbete Git →
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* CASE 3: Active Individual Chat View (Visitor view OR Owner in selected thread) */
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {/* Top Identity Sub-bar */}
                                <div className="flex items-center justify-between text-[11px] text-gray-500 px-2 pb-2.5 border-b border-gray-200/60 mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                        {isOwner ? (
                                            <span>
                                                Sohbet Edilen: <strong className="text-gray-900">{activeVisitor}</strong>
                                            </span>
                                        ) : (
                                            <span>
                                                Mesaj Gönderen: <strong className="text-gray-900">{nickname}</strong>
                                            </span>
                                        )}
                                    </div>
                                    {isOwner ? (
                                        <button
                                            type="button"
                                            onClick={() => setActiveVisitor(null)}
                                            className="text-blue-600 hover:underline font-semibold flex items-center gap-1"
                                        >
                                            <FaArrowLeft size={10} /> Kutulara Dön
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setHasSetNickname(false)}
                                            className="text-blue-600 hover:underline font-semibold"
                                        >
                                            Rumuz Değiştir
                                        </button>
                                    )}
                                </div>

                                {/* Message Stream */}
                                <div className="flex-1 overflow-y-auto space-y-3 p-3 bg-white rounded-2xl border border-gray-200 shadow-inner">
                                    {loadingMessages ? (
                                        <div className="h-full flex items-center justify-center text-gray-400 text-xs gap-2">
                                            <FaSpinner className="animate-spin text-blue-600" />
                                            <span>Mesajlar yükleniyor...</span>
                                        </div>
                                    ) : visibleMessages.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-center p-6 text-gray-400 text-xs">
                                            <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center mb-3 text-lg">
                                                <FaComments />
                                            </div>
                                            <p className="font-bold text-gray-700 text-sm">Bu kanalda henüz mesaj yok</p>
                                            <p className="text-[11px] mt-1 text-gray-400 max-w-xs">
                                                {isOwner
                                                    ? `${activeVisitor} kullanıcısına yanıt yazarak sohbeti başlatabilirsiniz.`
                                                    : 'Aşağıdaki hazır mesajlardan birini seçebilir ya da özel mesajınızı araç sahibine iletebilirsiniz.'}
                                            </p>
                                        </div>
                                    ) : (
                                        visibleMessages.map((msg) => {
                                            const isMine = isOwner ? msg.senderRole === 'owner' : msg.senderRole === 'visitor';
                                            const isCarOwner = msg.senderRole === 'owner';
                                            const visitorStyle = getNicknameStyle(msg.senderName);

                                            const timeStr = msg.timestamp?.toDate
                                                ? msg.timestamp.toDate().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                                                : '';

                                            return (
                                                <div
                                                    key={msg.id}
                                                    className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}
                                                >
                                                    <div className="flex items-center gap-1.5 mb-1 px-1">
                                                        {isCarOwner ? (
                                                            <span className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1 shadow-2xs">
                                                                <FaCar size={9} /> Araç Sahibi
                                                            </span>
                                                        ) : (
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-2xs ${visitorStyle.badge}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${visitorStyle.bg}`}></span>
                                                                {msg.senderName}
                                                            </span>
                                                        )}
                                                        {timeStr && (
                                                            <span className="text-[9px] text-gray-400">
                                                                {timeStr}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div
                                                        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-xs leading-relaxed shadow-xs transition-all ${
                                                            isMine
                                                                ? 'bg-blue-600 text-white rounded-br-xs'
                                                                : isCarOwner
                                                                ? 'bg-amber-50/90 text-gray-900 border border-amber-200 rounded-bl-xs'
                                                                : `${visitorStyle.lightBg} text-gray-900 border ${visitorStyle.border} rounded-bl-xs`
                                                        }`}
                                                    >
                                                        {msg.text}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Quick Presets for Visitors */}
                                {!isOwner && (
                                    <div className="flex flex-wrap gap-1.5 pt-2">
                                        {quickMessages.map((quick, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => handleSendMessage(quick)}
                                                disabled={sending}
                                                className="text-[11px] px-2.5 py-1 bg-white hover:bg-gray-100 text-gray-700 rounded-lg border border-gray-200 transition-colors font-medium text-left shadow-2xs cursor-pointer"
                                            >
                                                {quick}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Input Form */}
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }}
                                    className="flex gap-2 pt-2.5"
                                >
                                    <input
                                        type="text"
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        placeholder={isOwner ? `${activeVisitor} kullanıcısına yanıt yazın...` : "Mesajınızı yazın..."}
                                        maxLength={250}
                                        disabled={sending}
                                        className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-2xl text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs transition-all"
                                    />
                                    <button
                                        type="submit"
                                        disabled={sending || !inputText.trim()}
                                        className="px-5 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-md active:scale-95 cursor-pointer"
                                        aria-label="Mesaj Gönder"
                                    >
                                        {sending ? (
                                            <FaSpinner className="animate-spin" size={14} />
                                        ) : (
                                            <>
                                                <span className="hidden sm:inline">Gönder</span>
                                                <FaPaperPlane size={11} />
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );

        return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
    };

    return (
        <>
            {/* Floating Bubble trigger: portaled to document.body */}
            {!isOpen && renderFloatingBubble()}

            {/* Fullscreen Overlay modal: portaled to document.body like PurchasePage Shopier modal */}
            {isOpen && renderFullscreenModal()}
        </>
    );
};

export default QrDirectChat;
